import { PRIVILEGES } from 'constants/privileges';
import { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  useGetExternalServiceCodesByActivity,
  useDeleteExternalServiceCode,
  useUpdateExternalServiceCode,
} from 'api/externalServiceCode';
import { StyledButton } from 'components/Buttons';
import {
  CustomTablePagination,
  StyledTableCell,
  StyledTableHeadCell,
} from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { reactPaths } from 'root/pathnames';
import { appToast } from 'utils/toast.utils';
import { CounterpartyAutocomplete } from '../MappingForm/elements/CounterpartyAutocomplete';
import { CounterpartyOption } from '../MappingForm/elements/CounterpartySelect';
import {
  buttons,
  mainColumnHeaders,
  deleteExternalActivitySuccess,
  deleteExternalActivityError,
  updateExternalActivitySuccess,
  updateExternalActivityError,
  maxCodeLength,
} from '../MappingModule.consts';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import * as S from './MappingServiceDetails.css';
import { ServiceSideTable } from './ServiceSideTable';

interface CodeRow {
  id: string;
  code: string;
  counterparty: string;
  counterpartyId: number;
}

function renderWithBlanks(name: string): React.ReactNode {
  if (!name.includes('|')) return name;
  return name.split(/\|+/).reduce<React.ReactNode[]>((acc, part, i) => {
    if (i > 0)
      acc.push(
        <S.BlankRect
          key={`b${i}`}
          style={acc.length === 0 ? { marginLeft: 0 } : undefined}
        />,
      );
    if (part) acc.push(part);
    return acc;
  }, []);
}

export const MappingServiceDetails = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { isAuthorized } = useAuth();
  const canManage = isAuthorized(PRIVILEGES.Settings.MANAGE_DISCRIMINANT);
  const { t } = useTranslation(['mapping']);

  const {
    data: apiData,
    loading,
    getExternalServiceCodesByActivity,
  } = useGetExternalServiceCodesByActivity();

  const { deleteExternalServiceCode } = useDeleteExternalServiceCode();
  const { updateExternalServiceCode } = useUpdateExternalServiceCode();

  useEffect(() => {
    if (serviceId) getExternalServiceCodesByActivity(serviceId);
  }, [serviceId, getExternalServiceCodesByActivity]);

  const rows: CodeRow[] = (apiData ?? []).map((item) => ({
    id: String(item.id),
    code: item.externalName,
    counterparty: item.counterpartyCode,
    counterpartyId: item.counterpartyId,
  }));

  const activityCode = apiData?.[0]?.activityCode ?? '';

  // const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCode, setEditCode] = useState('');
  const [editCounterparty, setEditCounterparty] =
    useState<CounterpartyOption | null>(null);
  const [editErrors, setEditErrors] = useState({
    code: false,
    counterparty: false,
  });

  const handleConfirmEdit = async (row: CodeRow) => {
    if (!serviceId) return;
    const codeToSave = editCode;
    const codeError = !codeToSave.trim() || codeToSave.length > maxCodeLength;
    const counterpartyError = !editCounterparty;
    if (codeError || counterpartyError) {
      setEditErrors({ code: codeError, counterparty: counterpartyError });
      return;
    }
    try {
      const response = await updateExternalServiceCode(row.id, {
        activityId: Number(serviceId),
        counterpartyId: editCounterparty?.id ?? 0,
        externalName: codeToSave,
      });
      if ((response as any)?.data?.status === 'OK') {
        appToast.success(updateExternalActivitySuccess);
        setEditingId(null);
        setEditCode('');
        setEditCounterparty(null);
        setEditErrors({ code: false, counterparty: false });
        getExternalServiceCodesByActivity(serviceId);
      }
    } catch {
      appToast.error(updateExternalActivityError);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditCode('');
    setEditCounterparty(null);
    setEditErrors({ code: false, counterparty: false });
  };

  const paged = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelected(event.target.checked ? paged.map((row) => row.id) : []);

  const handleSelect = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    setSelected((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );
  };

  const activitiesUrl = `/tables/activities/${serviceId}`;

  return (
    <Box sx={{ display: 'flex' }}>
      <S.DetailsContainer>
        <ServiceSideTable activeServiceId={serviceId ?? ''} />
        <S.DetailsWrapper>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
              }}
            >
              <CircularProgress size={60} />
            </Box>
          ) : (
            <>
              <S.TitleRow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <S.Title>{activityCode}</S.Title>
                  <IconButton
                    size='small'
                    component='a'
                    href={activitiesUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    disableRipple
                    sx={{ '&:hover': { backgroundColor: 'transparent' } }}
                  >
                    <FiExternalLink size={18} />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {/* <StyledTextField
                    placeholder='Szukaj'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size='small'
                    sx={{ minWidth: 200, width: 'auto' }}
                  /> */}
                  {canManage && (
                    <StyledButton
                      $primary
                      onClick={() => navigate(reactPaths.mappingForm)}
                    >
                      {buttons.create}
                    </StyledButton>
                  )}
                  {canManage && (
                    <StyledButton
                      $primary
                      disabled={selected.length === 0}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      {buttons.delete}
                    </StyledButton>
                  )}
                </Box>
              </S.TitleRow>

              <Divider />
              <Box
                sx={{
                  border: '1px solid rgba(224, 224, 224, 1)',
                  borderTop: 'none',
                }}
              >
                <CustomTablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, pag) => setPage(pag)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
                <Divider />
                <TableContainer>
                  <Table sx={{ minWidth: 500 }} size='medium'>
                    <TableHead>
                      <TableRow>
                        <StyledTableHeadCell padding='checkbox'>
                          <Checkbox
                            color='primary'
                            indeterminate={
                              selected.length > 0 &&
                              selected.length < paged.length
                            }
                            checked={
                              paged.length > 0 &&
                              selected.length === paged.length
                            }
                            onChange={handleSelectAll}
                          />
                        </StyledTableHeadCell>
                        <StyledTableHeadCell>
                          {mainColumnHeaders.externalActivity}
                        </StyledTableHeadCell>
                        <StyledTableHeadCell>
                          {mainColumnHeaders.counterparty}
                        </StyledTableHeadCell>
                        <StyledTableHeadCell
                          padding='none'
                          sx={{ width: 72 }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paged.map((row) => {
                        const isEditing = editingId === row.id;
                        const isHovered = hoveredId === row.id;
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredId(row.id)}
                            onMouseLeave={() => setHoveredId(null)}
                          >
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selected.includes(row.id)}
                                onClick={(event) => handleSelect(event, row.id)}
                              />
                            </TableCell>
                            <StyledTableCell height='condensed'>
                              {isEditing ? (
                                <TextField
                                  value={editCode}
                                  onChange={(event) =>
                                    setEditCode(event.target.value)
                                  }
                                  error={editErrors.code}
                                  multiline
                                  minRows={1}
                                  size='small'
                                  variant='outlined'
                                  fullWidth
                                  autoFocus
                                  onClick={(event) => event.stopPropagation()}
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      fontSize: '0.9rem',
                                      fontWeight: 500,
                                      padding: '4px 8px',
                                      minHeight: '37px',
                                      minWidth: '550px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(0,0,0,0.15)',
                                    },
                                  }}
                                />
                              ) : (
                                renderWithBlanks(row.code)
                              )}
                            </StyledTableCell>
                            <StyledTableCell height='condensed'>
                              {isEditing ? (
                                <Box
                                  sx={{
                                    '& .MuiInputBase-root': {
                                      minHeight: '37px',
                                    },
                                  }}
                                >
                                  <CounterpartyAutocomplete
                                    value={editCounterparty}
                                    onChange={(value) =>
                                      setEditCounterparty(value)
                                    }
                                    error={editErrors.counterparty}
                                  />
                                </Box>
                              ) : (
                                row.counterparty
                              )}
                            </StyledTableCell>
                            <TableCell
                              padding='none'
                              sx={{ width: 72, pr: 0.5, textAlign: 'right' }}
                            >
                              {isEditing ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  <IconButton
                                    size='small'
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleConfirmEdit(row);
                                    }}
                                  >
                                    <CheckIcon
                                      fontSize='small'
                                      sx={{ color: 'action.active' }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    size='small'
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleCancelEdit();
                                    }}
                                  >
                                    <CloseIcon
                                      fontSize='small'
                                      sx={{ color: 'action.active' }}
                                    />
                                  </IconButton>
                                </Box>
                              ) : (
                                canManage &&
                                (row.code.includes('|') ? (
                                  <Tooltip
                                    title={t('patternEditDisabled', {
                                      ns: 'mapping',
                                    })}
                                  >
                                    <span>
                                      <IconButton
                                        size='small'
                                        disabled
                                        sx={{
                                          opacity: isHovered ? 0.4 : 0,
                                          transition: 'opacity 0.15s',
                                        }}
                                      >
                                        <DriveFileRenameOutlineIcon
                                          fontSize='small'
                                          sx={{ color: 'action.disabled' }}
                                        />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <IconButton
                                    size='small'
                                    sx={{
                                      opacity: isHovered ? 1 : 0,
                                      transition: 'opacity 0.15s',
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setEditingId(row.id);
                                      setEditCode(row.code);
                                      setEditCounterparty(
                                        row.counterpartyId
                                          ? {
                                              id: row.counterpartyId,
                                              code: row.counterparty,
                                            }
                                          : null,
                                      );
                                    }}
                                  >
                                    <DriveFileRenameOutlineIcon
                                      fontSize='small'
                                      sx={{ color: 'action.active' }}
                                    />
                                  </IconButton>
                                ))
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <CustomTablePagination
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, pag) => setPage(pag)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            </>
          )}
        </S.DetailsWrapper>
      </S.DetailsContainer>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        count={selected.length}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={async () => {
          try {
            await Promise.all(
              selected.map((id) => deleteExternalServiceCode(id)),
            );
            appToast.success(deleteExternalActivitySuccess);
            setSelected([]);
            setDeleteDialogOpen(false);
            const remaining = rows.filter((row) => !selected.includes(row.id));
            if (remaining.length === 0) {
              navigate(reactPaths.mappingModule);
            } else if (serviceId) {
              getExternalServiceCodesByActivity(serviceId);
            }
          } catch {
            appToast.error(deleteExternalActivityError);
          }
        }}
      />
    </Box>
  );
};
